import {
  Box,
  Button,
  ButtonGroup,
  H3,
  H4,
  Inline,
  Input,
  P,
  Stack
} from "@adfenix/alexandria";
import { Modal } from "components/Modal";
import { useTranslation } from "react-i18next";
import Divider from "components/Divider";
import { useFieldArray, useForm } from "react-hook-form";
import { SellerService } from "@adfenix/types";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SellerServiceApi, ProgramServiceApi } from "api/services";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";

type Seller = { emailAddress: string; name: string; lastName: string };

interface InviteSellerForm {
  propertyId: string;
  sellers: Seller[];
}
interface Props {
  location?: string;
  propertyId: string;
  hqId: string;
  setInviteSellerModal?: (show: boolean) => void;
}

const {
  seller: { authV1SellerCreate: addSeller }
} = SellerServiceApi;
const {
  inviteSeller: { authV1InviteSellerCreate: inviteSeller }
} = ProgramServiceApi;

export const InviteSellerForm = ({
  location,
  propertyId,
  hqId,
  setInviteSellerModal
}: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const handleModalClose = () => {
    setInviteSellerModal
      ? setInviteSellerModal(false)
      : navigate(`/customer/hq/${hqId}/property`);
  };

  const { mutate, isLoading: isInvitingSeller } = useMutation(
    () => inviteSeller(hqId, propertyId),
    {
      onSuccess: () => {
        toast.success(t("seller.choice.inviteSeller.success"));
        handleModalClose();
      },
      onError: () => {
        toast.error(t("seller.choice.inviteSeller.error"));
      }
    }
  );

  const { mutate: addSellers, isLoading: isAddingSellers } = useMutation(
    ["add-sellers"],
    (data: SellerService.AddSellersRequest) => addSeller(data),
    {
      retry: false,
      onSuccess: () => {
        queryClient.invalidateQueries(["checkIfSellerExistsOnProperty"]);
        mutate();
      },
      onError: () =>
        toast.error(t("seller.choice.inviteSeller.addSeller.error"))
    }
  );

  const handleAddSeller = async (formData: { sellers: Seller[] }) => {
    const sellersWithLastName = formData.sellers.map(seller => {
      return {
        emailAddress: seller.emailAddress,
        name: seller.name.concat(" ", seller.lastName)
      };
    });
    addSellers({
      propertyId: propertyId ?? "",
      sellers: sellersWithLastName
    });
  };

  const { register, control, handleSubmit } = useForm<InviteSellerForm>({
    defaultValues: {
      propertyId: propertyId ?? "",
      sellers: [{ emailAddress: "", name: "", lastName: "" }]
    }
  });

  const { fields, append, remove } = useFieldArray({
    name: "sellers",
    control
  });

  return (
    <form onSubmit={handleSubmit(handleAddSeller)}>
      <Modal.Card.Head onClose={handleModalClose}>
        <Modal.Card.Title style={{ width: "100%", textAlign: "start" }}>
          {location ? (
            <H3>
              {t("seller.choice.inviteSeller.header.withLocation", {
                value: location
              })}
            </H3>
          ) : (
            <H3>{t("seller.choice.inviteSeller.header")}</H3>
          )}
          <Divider margin="24px 0 0 0" />
        </Modal.Card.Title>
      </Modal.Card.Head>
      <Modal.Card.Body>
        <Stack spacing={"s"}>
          <P>{t("seller.choice.inviteSeller.modal.description")}</P>
          {fields &&
            fields.map((field, index) => {
              return (
                <Stack spacing={"xs2"} key={field.id}>
                  <ButtonGroup
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <H4>
                      {t("seller.choice.inviteSeller.seller.label")} {index + 1}
                    </H4>
                    <Button
                      logLabel="remove-invite-seller"
                      variant="text"
                      disabled={fields.length === 1}
                      onClick={() => remove(index)}
                    >
                      <i className="uil uil-trash-alt is-size-5" />
                    </Button>
                  </ButtonGroup>
                  <Inline justifyContent="space-between" alignItems="center">
                    <Input
                      label={t("seller.choice.inviteSeller.name.label")}
                      required
                      {...register(`sellers.${index}.name`)}
                      placeholder={t(
                        "seller.choice.inviteSeller.name.placeholder"
                      )}
                    />
                    <Input
                      required
                      label={t("seller.choice.inviteSeller.lastName.label")}
                      {...register(`sellers.${index}.lastName`)}
                      placeholder={t(
                        "seller.choice.inviteSeller.lastName.placeholder"
                      )}
                    />
                  </Inline>
                  <Input
                    required
                    type="email"
                    label={t("seller.choice.inviteSeller.email.label")}
                    {...register(`sellers.${index}.emailAddress`)}
                    placeholder={t(
                      "seller.choice.inviteSeller.email.placeholder"
                    )}
                  />
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      logLabel="append-seller"
                      variant="text"
                      onClick={() =>
                        append({
                          name: "",
                          lastName: "",
                          emailAddress: ""
                        })
                      }
                    >
                      + {t("seller.choice.inviteSeller.addSeller.button")}
                    </Button>
                  </Box>
                </Stack>
              );
            })}
        </Stack>
      </Modal.Card.Body>
      <Modal.Card.Foot style={{ alignItems: "end" }}>
        <ButtonGroup>
          <Button
            variant="secondary"
            logLabel="invite-seller-modal-cancel"
            onClick={handleModalClose}
            disabled={isAddingSellers || isInvitingSeller}
          >
            {t("seller.choice.inviteSeller.modal.cancel")}
          </Button>
          <Button
            logLabel="invite-seller-modal-submit"
            type="submit"
            isLoading={isAddingSellers || isInvitingSeller}
          >
            {t("seller.choice.inviteSeller.button.submit")}
          </Button>
        </ButtonGroup>
      </Modal.Card.Foot>
    </form>
  );
};
